import type { InjectionKey } from 'vue'
/**
 * Decides what a click on a table body row should do.
 * It is plain TypeScript with no Vue, so the rules can be tested without mounting a table.
 */

/**
 * Elements that have their own job inside a row.
 * A click on one of them, or on anything inside one, never opens the row.
 * A page marks any element the list misses with `data-row-click-ignore`.
 * The one exception is a control marked `data-row-click-opens`: its click does its own job and
 * then reaches the row too. `isOnControlInRow` applies it.
 */
export const ROW_CLICK_IGNORE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="combobox"]',
  '[data-row-click-ignore]',
].join(', ')

/**
 * What a row click means.
 * `ignore` does nothing. `open` hands the click to the row: the page opens it, or selects it
 * with `rowGesture`.
 */
export type RowClickAction = 'ignore' | 'open'

/** An event's target as an element. A text node counts as its parent. */
export function targetElementOf(eventTarget: EventTarget | null): Element | null {
  if (eventTarget instanceof Element) return eventTarget
  if (eventTarget instanceof Node) return eventTarget.parentElement
  return null
}

/**
 * True when the event landed in a control that sits inside the row, such as a link or a button.
 * The row itself does not count, so a card that is role="button" is not a control in itself.
 * A control outside the row does not count either.
 * Otherwise a table placed inside a `label` would ignore every click.
 * A control marked `data-row-click-opens` does not count. Its click does its own job and then
 * opens the row too, as a custom list card's track count does: it asks for the Tracks section
 * and leaves the card to open the record.
 * Clicks, mousedowns and long presses all use it, so they agree on what a control is.
 */
export function isOnControlInRow(eventTarget: EventTarget | null, rowElement: Element): boolean {
  const target = targetElementOf(eventTarget)
  if (!target) return false
  const interactiveAncestor = target.closest(ROW_CLICK_IGNORE_SELECTOR)
  if (!interactiveAncestor || interactiveAncestor === rowElement) return false
  if (interactiveAncestor.hasAttribute('data-row-click-opens')) return false
  return rowElement.contains(interactiveAncestor)
}

/**
 * True when the click ends a drag that selected text in the row.
 * A selection elsewhere on the page, such as in the peek view, does not count.
 */
function endsTextSelectionInRow(rowElement: Element): boolean {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false
  const { anchorNode, focusNode } = selection
  return (
    (anchorNode !== null && rowElement.contains(anchorNode)) ||
    (focusNode !== null && rowElement.contains(focusNode))
  )
}

/** Cmd, Ctrl or Shift: the keys that select rows instead of opening one. */
export function isSelectionClick(event: MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey
}

/**
 * True when no modifier key was held: not cmd, ctrl, shift or alt.
 * A page's row click handler uses it to open a row on a plain click only.
 * Every page shares it, so they all treat the same keys as modifiers.
 */
export function isPlainClick(event: MouseEvent): boolean {
  return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

/**
 * What a row click asks of the selection.
 * `replace` selects only that row. `toggle` adds or removes it. `extend` selects the range from the anchor to it.
 */
export type RowGesture = 'replace' | 'toggle' | 'extend'

/**
 * The selection gesture a row click makes.
 * Shift extends, and wins when Cmd or Ctrl is held too. Cmd or Ctrl toggles. Anything else replaces.
 */
export function rowGesture(event: MouseEvent): RowGesture {
  if (event.shiftKey) return 'extend'
  if (event.metaKey || event.ctrlKey) return 'toggle'
  return 'replace'
}

/**
 * Whether a row click reaches the row. A click with another button, or on a control in the row,
 * is ignored. So is a plain click that ends a drag to select text in the row.
 * A Cmd, Ctrl or Shift click reaches the row even with a text range left behind, because a
 * Shift-click can leave one and it must still select the range of rows.
 */
export function classifyRowClick(event: MouseEvent, rowElement: Element): RowClickAction {
  if (event.button !== 0) return 'ignore'
  if (isOnControlInRow(event.target, rowElement)) return 'ignore'
  if (!isSelectionClick(event) && endsTextSelectionInRow(rowElement)) return 'ignore'
  return 'open'
}

/**
 * DataTable provides this to the cells it draws: it says whether a click on a given row opens that row in the peek view.
 * The ID cell reads it to hide its own "open in side peek" button, because the row click already opens the same record.
 * Outside a DataTable nothing provides it, so cells keep their button.
 */
export const ROW_OPENS_IN_PEEK: InjectionKey<(row: Record<string, unknown>) => boolean> = Symbol('rowOpensInPeek')
